// ...existing code...
/**
 * Credit Balance Display Component
 *
 * Shows user's current credit balance.
 * This is a read-only display component.
 *
 * ⚠️ Frontend never deducts credits. Backend is the single source of truth.
 */

import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { Coins, RefreshCw, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { getCreditLedger, formatLedgerEntry } from "@/services/creditService";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { CreditLedgerEntry, formatCredits } from "@/types/credits";
import { cn } from "@/lib/utils";

// Human-readable labels for activity types
const labelMap: Record<string, string> = {
  STANDARD_REPORT: "Standard Report",
  ECO_PREDICT: "EcoPredict Prediction",
};

function timeAgo(iso?: string) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
interface CreditBalanceDisplayProps {
  /** The user's ID */
  userId: string;
  /** Optional className for styling */
  className?: string;
  /** Whether to show the history popover */
  showHistory?: boolean;
  /** Whether to use compact mode */
  compact?: boolean;
}

export function CreditBalanceDisplay({
  userId,
  className,
  showHistory = true,
  compact = false,
}: CreditBalanceDisplayProps) {
  const { balance, loading, refresh, regular, bonus } =
    useCreditBalance(userId);
  // ...existing code...
  const [history, setHistory] = React.useState<CreditLedgerEntry[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyCleared, setHistoryCleared] = React.useState(false);

  // Reset historyCleared flag when popover opens to allow fresh fetching
  React.useEffect(() => {
    if (historyOpen) {
      setHistoryCleared(false);
    }
  }, [historyOpen]);

  // Fetch history when popover opens
  React.useEffect(() => {
    if (historyOpen && userId && history.length === 0 && !historyCleared) {
      setHistoryLoading(true);
      getCreditLedger(userId, 10)
        .then((entries) => {
          setHistory(entries);
          setHistoryLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching credit history:", err);
          setHistoryLoading(false);
        });
    }
  }, [historyOpen, userId, history.length, historyCleared]);

  // ...existing code...

  const handleRefresh = async () => {
    try {
      await refresh();
    } catch (err) {
      console.error("Error refreshing credit balance:", err);
    }
  };

  // Refresh balance when a credit-consuming job completes elsewhere in the app.
  // We use a ref to ensure the latest `refresh` implementation is called from
  // the window event listener (this avoids stale closures).
  const refreshRef = React.useRef(refresh);
  React.useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  React.useEffect(() => {
    const onCreditsConsumed = () => {
      void refreshRef.current?.();
    };
    window.addEventListener("credits:consumed", onCreditsConsumed);
    return () => {
      window.removeEventListener("credits:consumed", onCreditsConsumed);
    };
  }, []);

  if (compact) {
    return (
      <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "focus:outline-none focus:ring-2 focus:ring-[#40e0d0] focus:ring-offset-2 rounded-lg",
              className,
            )}
            title={
              balance === 0
                ? "Purchase credits to generate new outputs."
                : "Credits are used only when you generate new outputs."
            }
          >
            <Badge
              variant="outline"
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 border-[#40e0d0]/30 bg-[#40e0d0]/5",
              )}
            >
              <Coins className="h-3.5 w-3.5 text-[#40e0d0]" />
              <span className="text-sm font-medium">
                {loading ? "..." : (balance ?? 0)}
              </span>
              credits
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          {/* BalanceSummary */}
          <div className="p-4 pb-2">
            <h4 className="text-2xl font-semibold">
              {loading ? "..." : `${balance ?? 0} credits available`}
            </h4>
            <div className="text-xs text-muted-foreground mt-1 font-medium">
              <div className="font-medium">
                {(regular ?? 0).toFixed(1)} base credits
              </div>
              <div className="font-medium">
                {(bonus ?? 0).toFixed(1)} bonus credits{" "}
                <span className="text-muted-foreground">(used first)</span>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2"></div>
          </div>
          <Separator />

          {/* RecentUsage */}
          <div className="p-4 pt-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Recent Activity</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setHistory([]);
                  setHistoryCleared(true);
                }}
              >
                Clear
              </Button>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-4 mb-4">
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading...
                </span>
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No credit transactions yet.
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {history.map((entry) => {
                  const formatted = formatLedgerEntry(entry);
                  const label =
                    labelMap[entry.reason] ||
                    entry.reason ||
                    formatted.description;
                  const abs = Math.abs(entry.creditsDelta ?? 0);
                  return (
                    <div
                      key={entry.id}
                      className="flex items-start justify-between text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {timeAgo(entry.createdAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <div
                          className={cn(
                            "font-medium",
                            entry.creditsDelta > 0
                              ? "text-green-500"
                              : "text-muted-foreground",
                          )}
                        >
                          {" "}
                          {abs}
                        </div>
                        {entry["credits_type"] === "bonus" && (
                          <div className="text-xs text-muted-foreground">
                            bonus
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Separator className="mt-6" />
            {/* Actions */}
            <div className="mt-3 flex items-center justify-between">
              <Button size="sm" asChild>
                <Link to="/purchase-credits">Buy credits</Link>
              </Button>
              <Link
                to="/dashboard/usage"
                className="text-xs text-muted-foreground hover:underline"
              >
                View usage details
              </Link>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Inner balance card (no interactive child buttons) — used as popover trigger
  const balanceInner = (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border border-[#40e0d0]/30 bg-[#40e0d0]/5 px-3 py-2",
        className,
      )}
      title={
        balance === 0
          ? "Purchase credits to generate new outputs."
          : "Credits are used only when you generate new outputs."
      }
    >
      <Coins className="h-4 w-4 text-[#40e0d0]" />
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">Credits available</span>
        <span className="text-lg font-semibold leading-none">
          {loading ? "..." : (balance ?? 0)}
        </span>
        <span className="text-xs text-muted-foreground">
          Bonus: {bonus ?? 0}
        </span>
      </div>
    </div>
  );

  // Full display including the refresh button (used when popover isn't needed)
  const balanceDisplay = (
    <div className="flex items-center gap-2">
      {balanceInner}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 ml-1"
        onClick={handleRefresh}
        disabled={loading}
      >
        <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        <span className="sr-only">Refresh balance</span>
      </Button>
    </div>
  );

  if (!showHistory) {
    return balanceDisplay;
  }

  // Only show transaction log, not usage summary
  return (
    <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
      <div className="inline-flex items-center gap-2">
        <PopoverTrigger asChild>
          <button className="focus:outline-none focus:ring-2 focus:ring-[#40e0d0] focus:ring-offset-2 rounded-lg">
            {balanceInner}
          </button>
        </PopoverTrigger>

        {/* Keep refresh button outside of the trigger to avoid nested buttons */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          <span className="sr-only">Refresh balance</span>
        </Button>
      </div>

      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Credit Balance</h4>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-[#40e0d0]">
                {balance ?? 0}
              </span>
              <Link
                to="/purchase-credits"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#40e0d0] text-white text-sm"
              >
                Purchase
              </Link>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <div>{(regular ?? 0).toFixed(1)} base credits</div>
            <div>
              {(bonus ?? 0).toFixed(1)} bonus credits{" "}
              <span className="text-muted-foreground">(used first)</span>
            </div>
          </div>
        </div>
        <Separator />
        <div className="p-4 pt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Recent Activity</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setHistory([]);
                setHistoryCleared(true);
              }}
            >
              Clear
            </Button>
          </div>
          {historyLoading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading...
              </span>
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No credit transactions yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((entry) => {
                const formatted = formatLedgerEntry(entry);
                return (
                  <div
                    key={entry.id}
                    className="flex items-start justify-between text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {formatted.description}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "font-medium ml-2 flex-shrink-0",
                        formatted.type === "purchase"
                          ? "text-green-500"
                          : "text-muted-foreground",
                      )}
                    >
                      {formatted.amount}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CreditBalanceDisplay;
